import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import PersonOutline from '@mui/icons-material/PersonOutline';
import School from '@mui/icons-material/School';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

export const StudentSelector: React.FC = () => {
  const { parent, selectStudent, needsStudentSelection, openStudentSelector, closeStudentSelector } = useAuth();
  const { students: mockStudents } = useData();
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);

  // Tour bridge: step 3 highlight 2 fires `open-student-selector` so the
  // demo can call out the dialog. Any other trigger (or step move-on)
  // closes it again so the dialog doesn't linger past step 3.
  useEffect(() => {
    const handler = (e: Event) => {
      const trigger = (e as CustomEvent<{ trigger?: string }>).detail?.trigger;
      if (trigger === 'open-student-selector') {
        openStudentSelector();
      } else {
        closeStudentSelector();
      }
    };
    window.addEventListener('cc-tour-trigger', handler);
    return () => window.removeEventListener('cc-tour-trigger', handler);
  }, [openStudentSelector, closeStudentSelector]);

  if (!needsStudentSelection || !parent) {
    return null;
  }

  const availableStudents = mockStudents.filter(s => parent.studentIds.includes(s.id));

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudentId(studentId);
    setTimeout(() => {
      selectStudent(studentId);
    }, 300);
  };

  const getPricingTierLabel = (tier: string) => {
    switch (tier) {
      case 'full':
        return 'Full Price';
      case 'reduced':
        return 'Reduced Price';
      case 'free':
        return 'Free';
      default:
        return tier;
    }
  };

  const getPricingTierColor = (tier: string) => {
    switch (tier) {
      case 'full':
        return 'primary';
      case 'reduced':
        return 'warning';
      case 'free':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={needsStudentSelection}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      slotProps={{ paper: { 'data-tour': 'student-selector-dialog' } as Record<string, unknown> }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
        },
        // When the tour is showing a bottom rail card, padding-bottom
        // shrinks the centring area so the dialog stays optically
        // centred between the top of the screen and the rail.
        // `--tour-bottom-rail-height` is set/unset by TourOverlay; the
        // fallback keeps non-tour usage centred normally.
        '& .MuiDialog-container': {
          paddingBottom: 'var(--tour-bottom-rail-height, 0px)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              mb: 2,
            }}
          >
            <School sx={{ fontSize: 36, color: 'white' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Select a Student
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which student you'd like to manage fees for
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {availableStudents.length === 0 ? (
          <Alert severity="warning">
            No students found for your account. Please contact school administration.
          </Alert>
        ) : (
          <Grid container spacing={2} sx={{ pb: 2 }}>
            {availableStudents.map((student) => (
              <Grid size={{ xs: 12, sm: 6 }} key={student.id}>
                <Card
                  elevation={selectedStudentId === student.id ? 8 : 2}
                  sx={{
                    transition: 'all 0.3s ease',
                    border: '2px solid',
                    borderColor: selectedStudentId === student.id ? 'primary.main' : 'transparent',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSelectStudent(student.id)}
                    sx={{ p: 2 }}
                  >
                    <CardContent>
                      {selectedStudentId === student.id && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                          }}
                        >
                          <CheckCircle color="primary" sx={{ fontSize: 32 }} />
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'primary.lighter',
                            mr: 2,
                          }}
                        >
                          <PersonOutline sx={{ fontSize: 32, color: 'primary.main' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            {student.firstName} {student.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Grade {student.grade}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={getPricingTierLabel(student.pricingTier)}
                          color={getPricingTierColor(student.pricingTier) as any}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};
